import axios from 'axios'
import { showToast } from './showToast'

type TranslateFn = (key: string) => string

const DELETE_ACCOUNT_ERROR_KEYS = new Set([
    'ORGANIZER_HAS_ACTIVE_FORMATS',
    'PARTICIPANT_HAS_NON_DRAFT_PARTICIPATIONS',
])

interface StrapiErrorShape {
    error?: { message?: string; details?: { code?: string } }
    message?: string
    data?: { messages?: { message?: string }[] }[]
}

export const extractErrorMessage = (error: unknown): string => {
    if (!axios.isAxiosError<StrapiErrorShape>(error)) {
        return error instanceof Error ? error.message : String(error)
    }

    const payload = error.response?.data
    if (payload) {
        const message =
            (typeof payload.error === 'object' && payload.error?.message) ||
            (typeof payload.error === 'string' && payload.error) ||
            payload.message ||
            payload.data?.[0]?.messages?.[0]?.message

        if (message?.trim()) return message
    }

    return error.message || 'Unknown error'
}

export const extractErrorDetailsCode = (error: unknown): string | undefined => {
    if (!axios.isAxiosError<StrapiErrorShape>(error)) {
        return undefined
    }

    const payload = error.response?.data
    if (payload) {
        const code = payload.error?.details?.code

        if (typeof code === 'string' && code.trim()) return code
    }

    return undefined
}

export const extractDeleteAccountErrorMessage = (
    error: unknown,
    translate: TranslateFn
): string => {
    const genericMessage = translate('ui.profile.deleteAccount.error.GENERIC')
    const code = extractErrorDetailsCode(error)

    if (!code || !DELETE_ACCOUNT_ERROR_KEYS.has(code)) return genericMessage

    return translate(`ui.profile.deleteAccount.error.${code}`)
}

export const showStatusCodeErrorToast = (error: unknown): void => {
    const statusCode =
        axios.isAxiosError(error) && error.response ? error.response.status : undefined

    switch (statusCode) {
        case 401:
            showToast({
                severity: 'info',
                summaryKey: 'frontend.messages.error.sessionExpired.summary',
                detailKey: 'frontend.messages.error.sessionExpired.detail',
                fallbackSummary: 'Unauthorized',
                fallbackDetail: 'Please log in again.',
                error,
            })
            break
        case 403:
            showToast({
                severity: 'warn',
                summaryKey: 'frontend.messages.error.forbiddenError.summary',
                detailKey: 'frontend.messages.error.forbiddenError.detail',
                fallbackSummary: 'Forbidden',
                fallbackDetail: 'Access denied.',
                error,
            })
            break
        default:
            if (statusCode && statusCode >= 400 && statusCode < 500) {
                showToast({
                    severity: 'warn',
                    summaryKey: 'frontend.messages.error.clientError.summary',
                    detailKey: 'frontend.messages.error.clientError.detail',
                    fallbackSummary: 'Request error',
                    fallbackDetail: 'Bad request.',
                    error,
                })
                break
            }
            showToast({
                severity: 'error',
                summaryKey: 'frontend.messages.error.serverError.summary',
                detailKey: 'frontend.messages.error.serverError.detail',
                fallbackSummary: 'Server error',
                fallbackDetail: 'Please try again later.',
                error,
            })
            break
    }
}

// WHY: Network errors (no response) and 5xx mean the CMS itself is down, not that the user lacks access.
export const isServerError = (error: unknown): boolean => {
    if (!axios.isAxiosError(error)) return false
    if (!error.response) return error.code !== axios.AxiosError.ERR_CANCELED
    return error.response.status >= 500
}
